import { useMutation } from "@apollo/client/react";
import {
  CreateFolder,
  CreateFolderVariables,
  FolderType,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { Section } from "common/components/Section";
import { H3 } from "common/components/Typography";
import { markdownInput } from "common/input-types";
import gql from "graphql-tag";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

const MUTATION = gql`
  mutation CreateFolder($input: CreateFolderInput!) {
    createFolder(input: $input) {
      folder {
        id
        title
        description {
          content
          isMarkdown
        }
      }
    }
  }
`;

interface Props {
  parentId: string;
  folderType: FolderType;
  cancel(): void;
  complete(): void;
}

interface FormValues {
  title: string;
  description?: string;
}

function folderTypeNorwegianTitle(folderType: FolderType): string {
  switch (folderType) {
    case FolderType.SELF_ASSESSMENT_EXERCISES:
      return "selvtest";
    case FolderType.GENERIC:
      return "mappe";
    case FolderType.SELF_ASSESSMENT_EXERCISES_CONTAINER:
      return "selvtestmappe";
  }
}

export const FolderCreate: React.FC<React.PropsWithChildren<Props>> = ({
  parentId,
  folderType,
  cancel,
  complete,
}) => {
  const methods = useForm<FormValues>();

  const [mutate, { error, loading }] = useMutation<
    CreateFolder,
    CreateFolderVariables
  >(MUTATION);

  const onSubmit = async (input: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          parentId: parentId,
          type: folderType,
          title: input.title,
          selfAssessmentExercisesFolderOptions:
            folderType === FolderType.SELF_ASSESSMENT_EXERCISES
              ? {
                  numberOfExercisesToExtract: 20,
                }
              : null, // TODO: Extract number of exercises to config in backend
          description: input.description
            ? markdownInput(input.description)
            : null,
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  return (
    <Section>
      <H3>{`Ny ${folderTypeNorwegianTitle(folderType)}`}</H3>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Tittel"
            fieldProps={methods.register("title", {
              required: "Du må fylle ut tittel",
            })}
          />

          <MarkdownField<FormValues>
            name="description"
            title={`Beskrivelse av ${folderTypeNorwegianTitle(folderType)}`}
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit onCancel={cancel} isLoading={loading} />
        </form>
      </FormProvider>
    </Section>
  );
};
